import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";

export type DensityMode = "comfortable" | "compact";

interface DensityContextValue {
  density: DensityMode;
  isCompact: boolean;
  isAutoMode: boolean;
  setDensity: (mode: DensityMode | "auto") => void;
  toggleDensity: () => void;
}

const DensityContext = createContext<DensityContextValue | null>(null);

const STORAGE_KEY = "app-density-mode";
const SHORT_VIEWPORT_THRESHOLD = 800; // pixels

interface DensityProviderProps {
  children: ReactNode;
}

export function DensityProvider({ children }: DensityProviderProps) {
  const [density, setDensityState] = useState<DensityMode>(() => {
    if (typeof window === "undefined") return "comfortable";
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "compact" || stored === "comfortable") return stored;
    // Auto-detect based on viewport height
    return window.innerHeight < SHORT_VIEWPORT_THRESHOLD ? "compact" : "comfortable";
  });

  const [isAutoMode, setIsAutoMode] = useState(() => {
    if (typeof window === "undefined") return true;
    return !localStorage.getItem(STORAGE_KEY);
  });

  // Handle viewport resize for auto mode
  useEffect(() => {
    if (!isAutoMode) return;

    const handleResize = () => {
      const newDensity = window.innerHeight < SHORT_VIEWPORT_THRESHOLD ? "compact" : "comfortable";
      setDensityState(newDensity);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isAutoMode]);

  const setDensity = useCallback((mode: DensityMode | "auto") => {
    if (mode === "auto") {
      localStorage.removeItem(STORAGE_KEY);
      setIsAutoMode(true);
      const newDensity = window.innerHeight < SHORT_VIEWPORT_THRESHOLD ? "compact" : "comfortable";
      setDensityState(newDensity);
    } else {
      localStorage.setItem(STORAGE_KEY, mode);
      setIsAutoMode(false);
      setDensityState(mode);
    }
  }, []);

  const toggleDensity = useCallback(() => {
    const newDensity = density === "comfortable" ? "compact" : "comfortable";
    setDensity(newDensity);
  }, [density, setDensity]);

  const value: DensityContextValue = {
    density,
    isCompact: density === "compact",
    isAutoMode,
    setDensity,
    toggleDensity,
  };

  return (
    <DensityContext.Provider value={value}>
      {children}
    </DensityContext.Provider>
  );
}

export function useDensity() {
  const context = useContext(DensityContext);
  if (!context) {
    throw new Error("useDensity must be used within DensityProvider");
  }
  return context;
}

/**
 * Optional hook that returns default values if not within a DensityProvider.
 * Use this in components that may or may not be rendered within the provider.
 */
export function useDensityOptional(): DensityContextValue {
  const context = useContext(DensityContext);
  if (!context) {
    return {
      density: "comfortable",
      isCompact: false,
      isAutoMode: true,
      setDensity: () => {},
      toggleDensity: () => {},
    };
  }
  return context;
}

export default DensityContext;
